import {
  HttpResponse,
  PrintingOrderJsonldPrintingOrderRead,
  PrintingOrderParticipantDTOJsonldPrintingOrderParticipantWrite,
  PrintingPageJsonldPrintingPageWrite,
} from '@/api/generated';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useApi from 'hooks/useApi';
import printingFactory from 'utils/factories/printing';

const usePrintOnDemandeProduct = () => {
  const apiClient = useApi();
  const queryKey = printingFactory.printOnDemandeProducts();
  const printOnDemandProducts = useQuery({
    queryKey,
    queryFn: () => apiClient.printOnDemandProducts.apiPrintOnDemandProductsGetCollection(),
  });

  return printOnDemandProducts;
};

export default usePrintOnDemandeProduct;

export const usePrintingOrders = ({ id }: { id: string }) => {
  const apiClient = useApi();

  return useQuery({
    queryKey: printingFactory.details(id),
    queryFn: () => apiClient.printingOrders.apiPrintingOrdersIdGet(id),
  });
};

export const useDeleteCardPrinting = ({ orderId, onSettled }: { orderId: string; onSettled?: () => void }) => {
  const apiClient = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['deleteCard'],
    mutationFn: (id: string) => apiClient.printingPageCards.apiPrintingPageCardsIdDelete(id),
    onMutate: (id) => {
      queryClient.cancelQueries({ queryKey: printingFactory.details(orderId) });
      // optimistically delete the card
      queryClient.setQueryData(
        printingFactory.details(orderId),
        (oldData: HttpResponse<PrintingOrderJsonldPrintingOrderRead, any>) => {
          const updatedData = { ...oldData };
          const page = updatedData.data?.printingPages?.find((p) => p.printingPageCards?.some((pc) => pc.id === id));
          if (page?.printingPageCards) {
            page.printingPageCards = page.printingPageCards?.filter((pc) => pc.id !== id);
          }

          return updatedData;
        },
      );
    },
    onSettled: () => {
      if (onSettled) {
        onSettled();
      }
      queryClient.invalidateQueries({ queryKey: printingFactory.details(orderId) });
    },
  });
};

export const useDeletePrintingPage = ({ orderId }: { orderId: string }) => {
  const apiClient = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['deletePage'],
    mutationFn: (id: string) => apiClient.printingPages.apiPrintingPagesIdDelete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: printingFactory.details(orderId) }),
  });
};

export const useAddPodParticipant = ({ orderId }: { orderId: string }) => {
  const apiClient = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['addPodParticipant'],
    mutationFn: (data: PrintingOrderParticipantDTOJsonldPrintingOrderParticipantWrite) =>
      apiClient.printingOrderParticipants.apiPrintingOrderParticipantsPost(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: printingFactory.details(orderId) }),
  });
};

export const useDeletePodParticipant = ({ orderId }: { orderId: string }) => {
  const apiClient = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['deletePodParticipant'],
    mutationFn: ({ printingOrder, user }: { printingOrder: string; user: string }) =>
      apiClient.printingOrderParticipants.apiPrintingOrderParticipantsPrintingOrderparticipantsUserDelete(
        printingOrder,
        user,
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: printingFactory.details(orderId) }),
  });
};

export const usePrintOnDemandProducts = () => {
  const apiClient = useApi();

  return useQuery({
    queryKey: printingFactory.pageType(),
    queryFn: async () => (await apiClient.printOnDemandProducts.apiPrintOnDemandProductsGetCollection()).data,
  });
};

export const useCreatePrintingPage = ({ orderId }: { orderId: string }) => {
  const apiClient = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['createPage'],
    mutationFn: (data: { printOnDemandProduct: string; name?: string }) =>
      apiClient.printingPages.apiPrintingPagesPost({
        ...data,
        printingOrder: `/printing_orders/${orderId}`,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: printingFactory.details(orderId) }),
  });
};

export const useUpdatePrintingPage = ({ orderId }: { orderId: string }) => {
  const apiClient = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['updatePage'],
    mutationFn: ({ id, data }: { id: string; data: PrintingPageJsonldPrintingPageWrite }) =>
      apiClient.printingPages.apiPrintingPagesIdPut(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: printingFactory.details(orderId) }),
  });
};
